import {
  WorkflowConditionLogic,
  WorkflowConditionOperator,
  WorkflowTriggerEntityType,
  WorkflowTriggerEventType,
  type WorkflowCondition,
  type WorkflowTrigger,
} from '@/gen/cortado/v1/cortado_pb'
import { EVENT_METADATA, triggerLeadTimeSummary } from './trigger-events'

const SUMMARY_MAX_DEPTH = 2
const SUMMARY_MAX_LENGTH = 96

// The sentinel formatConditionNode returns for "no conditions" — a definition
// that fires on every trigger event. Shared so callers can detect it rather
// than string-matching a literal.
const ALWAYS = 'Always'

export function formatWorkflowConditions(condition?: WorkflowCondition): {
  summary: string
  title: string
} {
  const title = formatConditionNode(condition, Number.POSITIVE_INFINITY)
  const compact = formatConditionNode(condition, SUMMARY_MAX_DEPTH)

  return {
    summary: truncate(compact, SUMMARY_MAX_LENGTH),
    title,
  }
}

/**
 * formatTriggerFilterSummary renders the trigger's filter state as the second
 * half of its one-line sentence ("Lead created" / "Every time").
 *
 * A definition with no entry filters fires on every trigger event — the trigger
 * IS the condition — so say that outright rather than leaving the trigger node
 * blank and reading as unfinished. Anything else renders as a narrowing clause.
 */
export function formatTriggerFilterSummary(condition?: WorkflowCondition): string {
  const { summary } = formatWorkflowConditions(condition)
  return summary === ALWAYS ? 'Every time' : `Only if ${summary}`
}

export function formatWorkflowTrigger(trigger?: WorkflowTrigger): string {
  if (!trigger) return 'Trigger not set'

  const label = eventTypeLabel(trigger.eventType)
  if (label) return withLeadTime(label, trigger)

  const entity = entityTypeLabel(trigger.entityType)
  if (entity) return entity

  return 'Trigger not set'
}

/**
 * withLeadTime appends the configured lead time to a relative-time event's
 * label ("Quote expiring soon · 3 days before"), so the list view and canvas
 * distinguish two workflows on the same event with different lead times. Events
 * that fire immediately (and an unconfigured lead time) render the bare label.
 */
function withLeadTime(label: string, trigger: WorkflowTrigger): string {
  return triggerLeadTimeSummary(trigger.eventType, label, trigger.daysBefore) ?? label
}

function formatConditionNode(
  condition: WorkflowCondition | undefined,
  maxDepth: number,
  depth = 0
): string {
  if (!condition || condition.kind.case === undefined) {
    return ALWAYS
  }

  if (condition.kind.case === 'leaf') {
    return formatConditionLeaf(condition.kind.value)
  }

  const group = condition.kind.value
  if (group.conditions.length === 0) {
    return ALWAYS
  }

  if (depth >= maxDepth) {
    return `${group.conditions.length} nested ${pluralize('condition', group.conditions.length)}`
  }

  const logic = logicLabel(group.logic)
  const joined = group.conditions
    .map((child) => formatConditionNode(child, maxDepth, depth + 1))
    .join(` ${logic} `)

  return depth > 0 ? `(${joined})` : joined
}

function formatConditionLeaf(
  leaf: Extract<WorkflowCondition['kind'], { case: 'leaf' }>['value']
): string {
  const field = formatFieldLabel(leaf.field)
  const operator = operatorLabel(leaf.operator)
  const value = formatConditionValue(leaf.value)

  if (!field && !value) return operator
  if (!value) return `${field} ${operator}`
  if (!field) return `${operator} ${value}`

  return `${field} ${operator} ${value}`
}

export function formatFieldLabel(field: string): string {
  return field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatConditionValue(value: string): string {
  const trimmed = value.trim()
  if (!/^[A-Za-z][A-Za-z0-9]*$/.test(trimmed)) return trimmed

  return trimmed.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
}

function logicLabel(logic: WorkflowConditionLogic): string {
  switch (logic) {
    case WorkflowConditionLogic.OR:
      return 'OR'
    case WorkflowConditionLogic.AND:
    case WorkflowConditionLogic.UNSPECIFIED:
    default:
      return 'AND'
  }
}

function operatorLabel(operator: WorkflowConditionOperator): string {
  switch (operator) {
    case WorkflowConditionOperator.EQUALS:
      return 'equals'
    case WorkflowConditionOperator.NOT_EQUALS:
      return 'does not equal'
    case WorkflowConditionOperator.CONTAINS:
      return 'contains'
    case WorkflowConditionOperator.NOT_CONTAINS:
      return 'does not contain'
    case WorkflowConditionOperator.GREATER_THAN:
      return 'is greater than'
    case WorkflowConditionOperator.LESS_THAN:
      return 'is less than'
    case WorkflowConditionOperator.UNSPECIFIED:
    default:
      return 'matches'
  }
}

export function entityTypeLabel(entityType: WorkflowTriggerEntityType): string {
  switch (entityType) {
    case WorkflowTriggerEntityType.INQUIRY:
      return 'Lead'
    case WorkflowTriggerEntityType.BOOKING:
      return 'Booking'
    case WorkflowTriggerEntityType.CONTACT:
      return 'Contact'
    case WorkflowTriggerEntityType.INVOICE:
      return 'Invoice'
    case WorkflowTriggerEntityType.EVENT:
      return 'Event'
    case WorkflowTriggerEntityType.QUOTE:
      return 'Quote'
    case WorkflowTriggerEntityType.PAYMENT:
      return 'Payment'
    case WorkflowTriggerEntityType.PAYMENT_SCHEDULE:
      return 'Installment'
    case WorkflowTriggerEntityType.UNSPECIFIED:
    default:
      return ''
  }
}

function eventTypeLabel(eventType: WorkflowTriggerEventType): string {
  if (eventType === WorkflowTriggerEventType.UNSPECIFIED) return ''
  return EVENT_METADATA[eventType]?.label ?? ''
}

function pluralize(word: string, count: number): string {
  return count === 1 ? word : `${word}s`
}

function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value
  return `${value.slice(0, maxLength - 3).trimEnd()}...`
}
